import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { fetchCaseNotesByAllocationId } from "../../store/allocation/readyCasesSlice";
import { updateAllocatedCases } from "../../store/allocation/allocateSlice";

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  allocationId: number | null;
  caseId?: string | null;
};

const SOURCE_LABEL: Record<string, string> = {
  internal: "Internal",
  allocation: "Allocation",
  redesign: "Redesign",
};

const CaseNotesModal = ({
  isOpen,
  onClose,
  allocationId,
  caseId,
}: ModalProps) => {
  const dispatch = useAppDispatch();
  const modalRef = useRef<HTMLDivElement>(null);
  const { user } = useAppSelector((state) => state.auth)
  const { employee } = useAppSelector((state) => state.allocationAuth)
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [passkey, setPasskey] = useState("");

  const { caseNotes, loading, error } = useAppSelector(
    (state) => state.readycases
  );
  const showPasskeyInput = (!user || user?.role !== "Super Admin") && (!employee);
  const canAddNote =
    Boolean(allocationId) &&
    (user?.role === "Super Admin" ||
      (Boolean(employee) && employee.role !== "CRM" && employee.role !== "Operations"));

  /* Fetch only when needed */
  useEffect(() => {
    if (isOpen && (allocationId || caseId)) {
      dispatch(fetchCaseNotesByAllocationId({ allocationId, caseId }));
    }
  }, [isOpen, allocationId, caseId, dispatch]);

  /* Lock body scroll */
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  /* Close on outside click */
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      onClose();
    }
  };

  /* Superfast memoized normalization */
  const notesArray = useMemo(() => {
    if (Array.isArray(caseNotes)) return caseNotes;
    if (caseNotes) return [caseNotes];
    return [];
  }, [caseNotes]);

  const handleAddNote = async () => {
    if (!canAddNote || !noteText.trim() || !allocationId) return;
    const isAdmin = user?.role === "Super Admin";
    try {
      setSubmitting(true);
      if (isAdmin || showPasskeyInput) {
        const finalPasskey = isAdmin ? user?.user_name : passkey;
        if (!finalPasskey) return;

        await dispatch(
          updateAllocatedCases({
            passkey: finalPasskey,
            isAdmin,
            updateCases: [
              {
                allocationId,
                action: "addNote",
                action_note: noteText.trim(),
              },
            ],
          })
        ).unwrap();
      } else {
        if (!employee?.emp_id) return;

        await dispatch(
          updateAllocatedCases({
            empId: employee.emp_id,
            isAdmin,
            updateCases: [
              {
                allocationId,
                action: "addNote",
                action_note: noteText.trim(),
              },
            ],
          })
        ).unwrap();
      }

      setNoteText("");
      dispatch(fetchCaseNotesByAllocationId({ allocationId, caseId }));
    } catch (e) {
      console.error(e);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
    >
      <div
        ref={modalRef}
        className="bg-white relative rounded-2xl w-[90%] max-w-4xl max-h-[70vh] flex flex-col"
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-2xl rounded-full px-2 hover:bg-gray-200"
        >
          &times;
        </button>


        {canAddNote ? (
        <div className="mb-4 border rounded-lg p-3 bg-gray-50">
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a note..."
            className="w-full border rounded-md p-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
            rows={3}
          />

          <div className="flex justify-end mt-2">
            <div className="flex items-center justify-between mt-2 gap-2">
              {/* Left side: passkey (only when no user & no employee) */}
              {showPasskeyInput && (
                <input
                  type="text"
                  value={passkey}
                  onChange={(e) => setPasskey(e.target.value)}
                  placeholder="Enter passkey"
                  className="border rounded-md px-2 py-1.5 text-sm w-48 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              )}

              {/* Right side: button */}
              <button
                onClick={handleAddNote}
                disabled={
                  submitting ||
                  !noteText.trim() ||
                  (showPasskeyInput && !passkey.trim())
                }
                className="px-4 py-1.5 text-sm rounded-md bg-blue-600 text-white disabled:opacity-50"
              >
                {submitting ? "Adding..." : "Add Note"}
              </button>
            </div>
          </div>
        </div>
        ) : null}

        {/* Content */}
        <div className="p-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
          {loading && <div className="text-center py-6">Loading...</div>}

          {error && (
            <div className="text-center text-red-500 py-2">{error}</div>
          )}

          {!loading && !error && (
            <>
              {notesArray.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  No case notes found
                </div>
              ) : (
                <div className="space-y-2">
                  {notesArray.map((item, index) => {
                    const source = item.source || "allocation";
                    const author = item.noted_by || item.author || "-";
                    const when = item.created_at || item.creation_time;
                    return (
                      <div
                        key={index}
                        className="border rounded-lg p-2 shadow-sm bg-gray-50"
                      >
                        <div className="flex justify-between items-center">
                          <span className="font-semibold text-gray-700">
                            #{index + 1} • {author}
                            <span className="ml-2 text-[10px] font-medium uppercase tracking-wide text-gray-500">
                              {SOURCE_LABEL[source] || source}
                            </span>
                          </span>
                          <span className="text-sm text-gray-500">
                            {when}
                          </span>
                        </div>

                        <p className="text-gray-800 mb-3">
                          {item.note === "Case allocated" && item.allocator
                            ? `Allocated by ${item.allocator}`
                            : item.note === "No note provided" &&
                                item.allocator &&
                                !String(item.QC2 || "").trim()
                              ? `Allocated by ${item.allocator}`
                              : item.note}
                        </p>

                        {source === "allocation" ? (
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-1 text-sm text-gray-600">
                            <div><b>Allocator:</b> {item.allocator || "-"}</div>
                            <div><b>QC1:</b> {item.QC1 || "-"}</div>
                            <div><b>Designer:</b> {item.Designer || "-"}</div>
                            <div><b>QC2:</b> {item.QC2 || "-"}</div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseNotesModal;
